import { Col, Row } from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDeleteCartMutation, useGetCartQuery, useUpdateCartMutation } from '../../../../apis/cart';
import { Card, EmptyState, Skeleton, notification } from '../../../../components/common';
import { Main } from '../../../../container/styled';
import { usePageTitle, useRouteNavigate } from '../../../../hooks';
import { getAppSource } from '../../../../store/parentApi';
import { useGetBundlesQuery } from '../../../../apis/bundles';
import useEmptyCartOnMount from '../../hooks/useEmptyCartOnMount';
import BundleOrderSummary from './BundleOrderSummary';
import CategorySection from './CategorySection';
import { BuyBundlesWrapper } from './styled';

const BuyBundles = () => {
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const user = useSelector((state) => state.app.loginUser.user);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  usePageTitle(t('Buy Bundles - Profolio'), t('Buy discounted credit bundles for your listings.'));

  const {
    data: bundlesData,
    error: bundlesError,
    isLoading: isBundlesLoading,
    refetch: refetchBundles,
  } = useGetBundlesQuery();
  const {
    data: cartData,
    error: cartError,
    isLoading: isCartLoading,
    isFetching: isCartFetching,
    refetch: refetchCart,
  } = useGetCartQuery({ userId: user.id }, { refetchOnMountOrArgChange: true });

  const [updateCart, { isLoading: isAddingBundle }] = useUpdateCartMutation();
  const [deleteCart, { isLoading: isRemovingBundle }] = useDeleteCartMutation();

  const isEmptyingCartOnMount = useEmptyCartOnMount(cartData, isCartFetching);

  // A card must stay disabled from click until the cart has actually re-fetched and settled —
  // clearing it as soon as the mutation call itself resolves leaves a window where the button
  // re-enables before cartLine reflects the change, letting a fast repeat click add a duplicate.
  const isCartBusy = isCartFetching || isAddingBundle || isRemovingBundle || isEmptyingCartOnMount;

  // Don't let the stale, about-to-be-emptied cart flash on screen before the mount-time
  // empty-cart flow settles — treat it as still loading and withhold its data instead.
  const displayCartData = isEmptyingCartOnMount ? undefined : cartData;
  const displayCartLoading = isCartLoading || isEmptyingCartOnMount;

  useEffect(() => {
    if (bundlesError?.status === 403) {
      navigate('/dashboard');
    }
  }, [bundlesError]);

  const handleAdd = async (bundle) => {
    const response = await updateCart({
      productList: [{ item_id: bundle.package_id, item_type: 'Package', quantity: 1, source: getAppSource() }],
      updateRedux: false,
    });
    if (response.error) {
      notification.error(response.error);
    }
  };

  const handleRemove = async (cartLine) => {
    // Bundle cart lines carry no item_id/item_type — only `id` (this line's own record id)
    // is needed to destroy an existing nested cart_details_attributes entry.
    const response = await deleteCart({
      productList: [
        {
          id: cartLine.id,
          _destroy: true,
          source: getAppSource(),
        },
      ],
    });
    if (response.error) {
      notification.error(response.error);
    }
  };

  const handleCheckout = () => {
    navigate(`/checkout?cart_id=${cartData.cartId}`);
  };

  if (bundlesError?.status === 403) {
    return null;
  }

  return (
    <Main>
      <BuyBundlesWrapper>
        <Row gutter={isMobile ? 16 : 32} justify="center" style={{ rowGap: 16 }}>
          <Col xxl={13} lg={16} xs={24}>
            <div className="buy-bundles-header">
              <h1>{t('Value Bundles')}</h1>
              <p>{t('Save up to 48% on your credits by purchasing in bulk. Select a bundle below that fits your business needs.')}</p>
            </div>

            {isBundlesLoading ? (
              <Skeleton type="input" />
            ) : bundlesError ? (
              <EmptyState loading={isBundlesLoading} onClick={refetchBundles} />
            ) : !bundlesData?.available ? (
              <EmptyState title={t("Bundles aren't available in your area yet")} hideRetryButton />
            ) : (
              <div className="buy-bundles-categories">
                {bundlesData.categories.map((category) => (
                  <CategorySection
                    key={category.type}
                    category={category}
                    cartDetails={displayCartData?.bundleCartDetails}
                    isBusy={isCartBusy}
                    onAdd={handleAdd}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            )}
          </Col>
          <Col xxl={7} lg={8} xs={24}>
            <Card className="mb-24" bodyStyle={{ padding: isMobile ? 16 : 24 }}>
              <BundleOrderSummary
                cartData={displayCartData}
                isCartLoading={displayCartLoading}
                inError={cartError}
                refetch={refetchCart}
                isBusy={isCartBusy}
                onRemove={handleRemove}
                onCheckout={handleCheckout}
              />
            </Card>
          </Col>
        </Row>
      </BuyBundlesWrapper>
    </Main>
  );
};

export default BuyBundles;
